/* Utility to normalize backend API error shapes into field + general errors.
 * Supports .NET style validation: { errors: { "$.dateOfUse": ["The JSON value ..."], "$.other": ["..."] } }
 * or { errors: { "dateOfUse": ["Required"] } }
 * or { message: "Something failed" }
 */
export interface ParsedApiErrors {
  fieldErrors: Record<string, string[]>;
  generalErrors: string[];
  firstMessage: string;
  raw: any;
}

function normalisePath(key: string): string {
  // Remove leading $. or $ and any brackets e.g. $.items[0].name -> items.0.name
  let cleaned = key.trim();
  if (cleaned.startsWith("$")) {
    cleaned = cleaned.replace(/^\$\.?/, "");
  }
  // Replace [index] with .index
  cleaned = cleaned.replace(/\[(\d+)\]/g, ".$1");
  return cleaned;
}

export function parseApiErrors(error: any): ParsedApiErrors {
  const parsed: ParsedApiErrors = {
    fieldErrors: {},
    generalErrors: [],
    firstMessage: "",
    raw: error
  };

  if (!error) return parsed;

  // Try typical shapes
  const responseData =
    error?.response?.data || // axios style
    error?.data ||
    error?.body ||
    error;

  const errorsContainer = responseData?.errors || responseData?.Errors;

  if (errorsContainer && typeof errorsContainer === "object") {
    Object.entries(errorsContainer).forEach(([key, value]) => {
      const msgs: string[] = Array.isArray(value)
        ? value.map(String)
        : [String(value)];
      const fieldKey = normalisePath(key);
      // If key still contains dots but first segment is likely model property we keep first segment
      const topLevel = fieldKey.split(".")[0];

      // Heuristic: consider as field error if field key length reasonable and not empty
      const fieldName = topLevel || fieldKey;
      if (!parsed.fieldErrors[fieldName]) parsed.fieldErrors[fieldName] = [];
      parsed.fieldErrors[fieldName].push(...msgs);
    });
  }

  // Fallback general message — supports string[] or string from backend
  const rawMsg =
    responseData?.message ?? responseData?.Message ?? error?.message ?? "";
  if (!parsed.generalErrors.length) {
    if (Array.isArray(rawMsg)) {
      rawMsg
        .filter(Boolean)
        .forEach((m: string) => parsed.generalErrors.push(String(m)));
    } else if (typeof rawMsg === "string" && rawMsg) {
      parsed.generalErrors.push(rawMsg);
    }
  }

  // Add any field messages also to general list for overall display
  Object.values(parsed.fieldErrors).forEach((arr) => {
    arr.forEach((m) => parsed.generalErrors.push(m));
  });

  parsed.firstMessage = parsed.generalErrors[0] || "";
  return parsed;
}

/**
 * Extracts all error messages from an API error and joins them.
 * Handles `{ message: string[] }` backend shape (standard PPS response).
 */
export function extractApiMessages(
  error: unknown,
  fallback = "An unexpected error occurred"
): string {
  const parsed = parseApiErrors(error);
  return parsed.generalErrors.length > 0
    ? parsed.generalErrors.join(" ")
    : fallback;
}

export function applyFieldErrorsToForm(form: any, parsed: ParsedApiErrors) {
  if (!form || !parsed) return;
  Object.entries(parsed.fieldErrors).forEach(([field, messages]) => {
    if (form.getFieldState && form.getFieldState(field)) {
      form.setError(field as any, { message: messages[0] });
    } else if (form.setError) {
      form.setError(field as any, { message: messages[0] });
    }
  });
}
